import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import UndoIcon from '@mui/icons-material/Undo';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useBrand } from '../contexts/BrandContext';
import { tasksApi } from '../services/tasks';
import type {
  Task,
  TaskCategory,
  TaskPersonaTab,
  TaskSourceType,
  TaskStatus,
} from '@traaker/shared';

type EffortBucket = 'quick' | 'medium' | 'complex';
type TimeFilter = 'all' | '7d' | '30d' | '90d';
type StatusFilter = TaskStatus | 'all';
type PersonaFilter = TaskPersonaTab | 'all';
type SourceFilter = TaskSourceType | 'all';
type PriorityFilter = Task['priority'] | 'all';

const PRIORITY_TO_IMPACT: Record<Task['priority'], number> = {
  critical: 95,
  high: 78,
  medium: 55,
  low: 30,
};

const PRIORITY_LABELS: Record<Task['priority'], string> = {
  critical: 'Critique',
  high: 'Haute',
  medium: 'Moyenne',
  low: 'Basse',
};

const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'À faire',
  in_progress: 'En cours',
  done: 'Terminée',
  dismissed: 'Écartée',
};

const STATUS_COLORS: Record<TaskStatus, { fg: string; bg: string }> = {
  todo: { fg: '#5F6470', bg: 'rgba(95, 100, 112, 0.10)' },
  in_progress: { fg: '#2563EB', bg: 'rgba(37, 99, 235, 0.10)' },
  done: { fg: '#059669', bg: 'rgba(5, 150, 105, 0.10)' },
  dismissed: { fg: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.10)' },
};

const EFFORT_LABELS: Record<EffortBucket, string> = {
  quick: 'Quick',
  medium: 'Medium',
  complex: 'Complex',
};

const EFFORT_COLORS: Record<EffortBucket, { fg: string; bg: string }> = {
  quick: { fg: '#059669', bg: 'rgba(5, 150, 105, 0.12)' },
  medium: { fg: '#D97706', bg: 'rgba(217, 119, 6, 0.12)' },
  complex: { fg: '#DC2626', bg: 'rgba(220, 38, 38, 0.12)' },
};

const PERSONA_LABELS: Record<TaskPersonaTab, string> = {
  technique: 'Technique',
  contenu: 'Contenu',
  hors_site: 'Hors-site',
};

const PERSONA_COLORS: Record<TaskPersonaTab, { fg: string; bg: string }> = {
  technique: { fg: '#4F46E5', bg: 'rgba(79, 70, 229, 0.10)' },
  contenu: { fg: '#7C3AED', bg: 'rgba(124, 58, 237, 0.10)' },
  hors_site: { fg: '#0891B2', bg: 'rgba(8, 145, 178, 0.10)' },
};

const CATEGORY_LABELS: Record<TaskCategory, string> = {
  technical: 'Tech Fix',
  schema: 'Schema',
  seo: 'SEO',
  content: 'Content',
  external: 'External',
  earned_media: 'Earned Media',
  social: 'Social',
};

const CATEGORY_COLORS: Record<TaskCategory, { fg: string; bg: string }> = {
  technical: { fg: '#0F766E', bg: 'rgba(15, 118, 110, 0.10)' },
  schema: { fg: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.10)' },
  seo: { fg: '#2563EB', bg: 'rgba(37, 99, 235, 0.10)' },
  content: { fg: '#7C3AED', bg: 'rgba(124, 58, 237, 0.10)' },
  external: { fg: '#DB2777', bg: 'rgba(219, 39, 119, 0.10)' },
  earned_media: { fg: '#EA580C', bg: 'rgba(234, 88, 12, 0.10)' },
  social: { fg: '#9333EA', bg: 'rgba(147, 51, 234, 0.10)' },
};

const SOURCE_LABELS: Record<TaskSourceType, string> = {
  audit_website: 'Website',
  audit_brand: 'Brand',
  audit_earned_media: 'Earned Media',
  manual: 'Manuel',
};

const VERIFICATION_COLORS: Record<string, string> = {
  verified: '#059669',
  not_fixed: '#DC2626',
  inconclusive: '#D97706',
  pending: '#6B7280',
};

const VERIFICATION_LABELS: Record<string, string> = {
  verified: 'Vérifiée',
  not_fixed: 'Non corrigée',
  inconclusive: 'Inconcluante',
  pending: 'Non vérifiée',
};

function bucketEffort(raw?: string): EffortBucket {
  if (!raw) return 'medium';
  const s = raw.toLowerCase();
  if (/min|≤\s*1h|< ?1h|^~?1h$|quick/.test(s)) return 'quick';
  if (/jour|day|developpeur|développeur|semaine|week|complex/.test(s)) return 'complex';
  return 'medium';
}

function impactOf(task: Task): number {
  return PRIORITY_TO_IMPACT[task.priority];
}

function isQuickWin(task: Task): boolean {
  return impactOf(task) >= 75 && bucketEffort(task.effortEstimate) === 'quick';
}

function withinTimeFilter(task: Task, filter: TimeFilter): boolean {
  if (filter === 'all') return true;
  const days = filter === '7d' ? 7 : filter === '30d' ? 30 : 90;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(task.createdAt).getTime() >= cutoff;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function simpleMarkdownToHtml(md: string): string {
  const codeBlocks: string[] = [];
  const inlineCodes: string[] = [];

  let result = md
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_, _lang, code) => {
      codeBlocks.push(`<pre style="background:rgba(0,0,0,0.15);padding:12px;border-radius:4px;overflow-x:auto;font-size:13px"><code>${escapeHtml(code)}</code></pre>`);
      return `\x00CB${codeBlocks.length - 1}\x00`;
    })
    .replace(/`([^`]+)`/g, (_, code) => {
      inlineCodes.push(`<code style="background:rgba(124,58,237,0.10);padding:1px 4px;border-radius:3px;font-size:13px">${escapeHtml(code)}</code>`);
      return `\x00IC${inlineCodes.length - 1}\x00`;
    })
    .replace(/^####\s+(.+)$/gm, '<h4 style="margin:16px 0 8px;font-size:14px;font-weight:700;color:#7C3AED">$1</h4>')
    .replace(/^###\s+(.+)$/gm, '<h3 style="margin:16px 0 8px;font-size:15px;font-weight:700;color:#7C3AED">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^[\s]*[-*]\s+(.+)$/gm, '<li style="margin:2px 0">$1</li>')
    .replace(/^[\s]*(\d+)\.\s+(.+)$/gm, '<li style="margin:4px 0" value="$1">$2</li>')
    .replace(/((?:<li[^>]*>.*<\/li>\s*)+)/g, '<ul style="margin:4px 0 12px 16px;padding:0;list-style:disc">$1</ul>')
    .replace(/^(?!<[a-z])(?!\x00)((?!^\s*$).+)$/gm, '<p style="margin:4px 0;line-height:1.6">$1</p>')
    .replace(/<p[^>]*>\s*<\/p>/g, '');

  result = result.replace(/\x00CB(\d+)\x00/g, (_, i) => codeBlocks[+i]);
  result = result.replace(/\x00IC(\d+)\x00/g, (_, i) => inlineCodes[+i]);
  return result;
}

export default function Tasks() {
  const { activeBrand } = useBrand();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [personaFilter, setPersonaFilter] = useState<PersonaFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [detailTask, setDetailTask] = useState<Task | null>(null);

  const loadTasks = useCallback(async () => {
    try {
      const data = await tasksApi.list(activeBrand?.brandId);
      setTasks(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.brandId]);

  useEffect(() => {
    setLoading(true);
    loadTasks();
  }, [loadTasks]);

  const handleStatusChange = async (taskId: string, status: TaskStatus) => {
    try {
      const updated = await tasksApi.updateStatus(taskId, status);
      setTasks((prev) => prev.map((t) => (t.taskId === taskId ? updated : t)));
      if (status === 'done') setFeedback('Tâche terminée !');
    } catch {
      setFeedback('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await tasksApi.delete(taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
      if (detailTask?.taskId === taskId) setDetailTask(null);
    } catch {
      setFeedback('Erreur lors de la suppression');
    }
  };

  const handleVerify = async (taskId: string) => {
    setFeedback('Vérification en cours...');
    try {
      const updated = await tasksApi.verify(taskId);
      setTasks((prev) => prev.map((t) => (t.taskId === taskId ? updated : t)));
      setFeedback(
        updated.verificationStatus === 'verified'
          ? 'Correction vérifiée !'
          : updated.verificationStatus === 'not_fixed'
            ? 'Problème toujours présent.'
            : 'Vérification inconcluante.',
      );
    } catch {
      setFeedback('Erreur lors de la vérification');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks
      .filter((t) => statusFilter === 'all' || t.status === statusFilter)
      .filter((t) => personaFilter === 'all' || t.personaTab === personaFilter)
      .filter((t) => sourceFilter === 'all' || t.sourceType === sourceFilter)
      .filter((t) => priorityFilter === 'all' || t.priority === priorityFilter)
      .filter((t) => withinTimeFilter(t, timeFilter))
      .filter((t) => {
        if (!q) return true;
        return (
          t.title.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          (t.sourcePageUrl ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => impactOf(b) - impactOf(a));
  }, [tasks, search, statusFilter, personaFilter, sourceFilter, priorityFilter, timeFilter]);

  const kpis = useMemo(() => {
    const quickWins = tasks.filter(isQuickWin).length;
    return {
      total: tasks.length,
      quickWins,
      pending: tasks.filter((t) => t.status === 'todo').length,
      inProgress: tasks.filter((t) => t.status === 'in_progress').length,
      done: tasks.filter((t) => t.status === 'done').length,
    };
  }, [tasks]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
        Tâches
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Centre d'action — recommandations issues de vos audits, classées par impact.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.includes('Erreur') ? 'error' : feedback.includes('présent') ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setFeedback('')}
        >
          {feedback}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label="Total Actions" value={kpis.total} color="#111827" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard
            label="Quick Wins"
            value={kpis.quickWins}
            color="#D97706"
            icon={<BoltIcon sx={{ fontSize: 18, color: '#F59E0B' }} />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label="Pending" value={kpis.pending} color="#EA580C" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label="In Progress" value={kpis.inProgress} color="#2563EB" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <KpiCard label="Completed" value={kpis.done} color="#059669" />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems="stretch">
            <TextField
              size="small"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              allLabel="Tous statuts"
              options={[
                ['todo', 'À faire'],
                ['in_progress', 'En cours'],
                ['done', 'Terminée'],
                ['dismissed', 'Écartée'],
              ]}
            />
            <FilterSelect
              value={personaFilter}
              onChange={setPersonaFilter}
              allLabel="Tous types"
              options={[
                ['technique', 'Technique'],
                ['contenu', 'Contenu'],
                ['hors_site', 'Hors-site'],
              ]}
            />
            <FilterSelect
              value={sourceFilter}
              onChange={setSourceFilter}
              allLabel="Toutes sources"
              options={[
                ['audit_website', 'Website'],
                ['audit_brand', 'Brand'],
                ['audit_earned_media', 'Earned Media'],
                ['manual', 'Manuel'],
              ]}
            />
            <FilterSelect
              value={priorityFilter}
              onChange={setPriorityFilter}
              allLabel="Toutes priorités"
              options={[
                ['critical', 'Critique'],
                ['high', 'Haute'],
                ['medium', 'Moyenne'],
                ['low', 'Basse'],
              ]}
            />
            <FilterSelect
              value={timeFilter}
              onChange={setTimeFilter}
              allLabel="Toute période"
              options={[
                ['7d', '7 derniers jours'],
                ['30d', '30 derniers jours'],
                ['90d', '90 derniers jours'],
              ]}
            />
          </Stack>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Aucune tâche
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tasks.length === 0
                ? 'Lancez un audit et ajoutez des recommandations en cliquant sur le bouton + à côté de chaque conseil.'
                : 'Aucune tâche ne correspond aux filtres.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TaskTable
          tasks={filtered}
          onOpenDetail={setDetailTask}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onVerify={handleVerify}
        />
      )}

      {detailTask && (
        <TaskDetailDialog
          task={detailTask}
          open
          onClose={() => setDetailTask(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onVerify={handleVerify}
        />
      )}
    </Box>
  );
}

function KpiCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          {icon}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ color, lineHeight: 1.1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function FilterSelect<T extends string>({
  value,
  onChange,
  allLabel,
  options,
}: {
  value: T | 'all';
  onChange: (v: T | 'all') => void;
  allLabel: string;
  options: [T, string][];
}) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value as T | 'all')}
      sx={{ minWidth: 150, bgcolor: 'background.paper' }}
    >
      <MenuItem value="all">{allLabel}</MenuItem>
      {options.map(([k, label]) => (
        <MenuItem key={k} value={k}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );
}

function TaskTable({
  tasks,
  onOpenDetail,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  tasks: Task[];
  onOpenDetail: (t: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  return (
    <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Recommandation
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, width: 110 }}>
              Impact
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, width: 110 }}>
              Effort
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, width: 130 }}>
              Status
            </TableCell>
            <TableCell align="right" sx={{ width: 130 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map((task) => (
            <TaskRow
              key={task.taskId}
              task={task}
              onOpenDetail={onOpenDetail}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              onVerify={onVerify}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TaskRow({
  task,
  onOpenDetail,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  task: Task;
  onOpenDetail: (t: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  const impact = impactOf(task);
  const effort = bucketEffort(task.effortEstimate);
  const quick = isQuickWin(task);
  const persona = PERSONA_COLORS[task.personaTab];
  const category = CATEGORY_COLORS[task.category];

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
      onClick={() => onOpenDetail(task)}
    >
      <TableCell sx={{ py: 1.25 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
          {task.title}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <TagChip label={PERSONA_LABELS[task.personaTab]} colors={persona} />
          <TagChip label={CATEGORY_LABELS[task.category]} colors={category} />
          {task.verificationStatus && task.verificationStatus !== 'pending' && (
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 12 }} />}
              label={VERIFICATION_LABELS[task.verificationStatus]}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: `${VERIFICATION_COLORS[task.verificationStatus]}1A`,
                color: VERIFICATION_COLORS[task.verificationStatus],
                '& .MuiChip-icon': { color: VERIFICATION_COLORS[task.verificationStatus] },
              }}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <ImpactGauge value={impact} />
          {quick && (
            <Tooltip title="Quick win">
              <BoltIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={EFFORT_LABELS[effort]}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: EFFORT_COLORS[effort].bg,
            color: EFFORT_COLORS[effort].fg,
            border: 'none',
          }}
        />
      </TableCell>
      <TableCell align="center">
        <StatusPill status={task.status} />
      </TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <RowActions
          task={task}
          onStatusChange={onStatusChange}
          onDelete={onDelete}
          onVerify={onVerify}
        />
      </TableCell>
    </TableRow>
  );
}

function TagChip({ label, colors }: { label: string; colors: { fg: string; bg: string } }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 600,
        bgcolor: colors.bg,
        color: colors.fg,
        border: 'none',
        '& .MuiChip-label': { px: 0.875 },
      }}
    />
  );
}

function ImpactGauge({ value }: { value: number }) {
  const theme = useTheme();
  const stroke =
    value >= 80 ? '#10B981' : value >= 60 ? '#7C3AED' : value >= 40 ? '#F59E0B' : '#9CA3AF';
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - value / 100);

  return (
    <Box sx={{ position: 'relative', width: 40, height: 40, display: 'inline-block' }}>
      <svg width={40} height={40} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={20}
          cy={20}
          r={radius}
          fill="none"
          stroke={theme.palette.divider}
          strokeWidth={3}
        />
        <circle
          cx={20}
          cy={20}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth={3}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: stroke }}>{value}</Typography>
      </Box>
    </Box>
  );
}

function StatusPill({ status }: { status: TaskStatus }) {
  const c = STATUS_COLORS[status];
  const Icon =
    status === 'in_progress'
      ? PlayArrowIcon
      : status === 'done'
        ? CheckCircleIcon
        : status === 'dismissed'
          ? BlockIcon
          : ScheduleIcon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 14 }} />}
      label={STATUS_LABELS[status]}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        bgcolor: c.bg,
        color: c.fg,
        border: 'none',
        '& .MuiChip-icon': { color: c.fg, ml: '6px' },
      }}
    />
  );
}

function RowActions({
  task,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  task: Task;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  return (
    <Stack direction="row" spacing={0} justifyContent="flex-end">
      {task.status === 'todo' && (
        <Tooltip title="Démarrer">
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'in_progress')}>
            <PlayArrowIcon fontSize="small" sx={{ color: '#2563EB' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'in_progress' && (
        <Tooltip title="Terminer">
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'done')}>
            <CheckCircleIcon fontSize="small" sx={{ color: '#059669' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'done' && task.sourcePageUrl && task.personaTab === 'technique' && (
        <Tooltip title="Vérifier la correction">
          <IconButton size="small" onClick={() => onVerify(task.taskId)}>
            <VerifiedIcon fontSize="small" sx={{ color: '#7C3AED' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'done' && (
        <Tooltip title="Rouvrir">
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'todo')}>
            <UndoIcon fontSize="small" sx={{ color: '#EA580C' }} />
          </IconButton>
        </Tooltip>
      )}
      {(task.status === 'todo' || task.status === 'in_progress') && (
        <Tooltip title="Écarter">
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'dismissed')}>
            <BlockIcon fontSize="small" sx={{ color: '#6B7280' }} />
          </IconButton>
        </Tooltip>
      )}
      {task.status === 'dismissed' && (
        <Tooltip title="Rouvrir">
          <IconButton size="small" onClick={() => onStatusChange(task.taskId, 'todo')}>
            <UndoIcon fontSize="small" sx={{ color: '#7C3AED' }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Supprimer">
        <IconButton size="small" onClick={() => onDelete(task.taskId)}>
          <DeleteOutlineIcon fontSize="small" sx={{ color: '#DC2626' }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function TaskDetailDialog({
  task,
  open,
  onClose,
  onStatusChange,
  onDelete,
  onVerify,
}: {
  task: Task;
  open: boolean;
  onClose: () => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
  onVerify: (taskId: string) => void;
}) {
  const impact = impactOf(task);
  const effort = bucketEffort(task.effortEstimate);
  const quick = isQuickWin(task);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
        <ImpactGauge value={impact} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {task.title}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            <TagChip label={PERSONA_LABELS[task.personaTab]} colors={PERSONA_COLORS[task.personaTab]} />
            <TagChip label={CATEGORY_LABELS[task.category]} colors={CATEGORY_COLORS[task.category]} />
            <Chip
              label={EFFORT_LABELS[effort]}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: EFFORT_COLORS[effort].bg,
                color: EFFORT_COLORS[effort].fg,
              }}
            />
            <Chip
              label={`Priorité : ${PRIORITY_LABELS[task.priority]}`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
            />
            {quick && (
              <Chip
                icon={<BoltIcon sx={{ fontSize: 12 }} />}
                label="Quick win"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                  color: '#D97706',
                  '& .MuiChip-icon': { color: '#F59E0B' },
                }}
              />
            )}
          </Stack>
        </Box>
        <StatusPill status={task.status} />
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {task.description}
        </Typography>

        {task.sourcePageUrl && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Page source</Typography>
            <Typography
              variant="body2"
              sx={{ color: '#7C3AED', cursor: 'pointer', wordBreak: 'break-all' }}
              onClick={() => window.open(task.sourcePageUrl, '_blank')}
            >
              {task.sourcePageUrl}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Source</Typography>
          <Typography variant="body2">{SOURCE_LABELS[task.sourceType]}</Typography>
        </Box>

        {task.howTo && (
          <Box sx={{ bgcolor: 'rgba(124,58,237,0.04)', borderRadius: 1.5, p: 2, border: '1px solid rgba(124,58,237,0.18)' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#7C3AED' }}>
              Comment faire ?
            </Typography>
            <Box
              sx={{ fontSize: '0.875rem', lineHeight: 1.6, '& pre': { my: 1 }, '& h3, & h4': { '&:first-of-type': { mt: 0 } } }}
              dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(task.howTo) }}
            />
          </Box>
        )}

        {task.verificationDetail && (
          <Box sx={{ mt: 2, bgcolor: `${VERIFICATION_COLORS[task.verificationStatus || 'pending']}0F`, borderRadius: 1.5, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Résultat de la vérification
            </Typography>
            <Typography variant="body2">{task.verificationDetail}</Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Créée le {new Date(task.createdAt).toLocaleDateString('fr-FR')}
          </Typography>
          {task.updatedAt !== task.createdAt && (
            <Typography variant="caption" color="text.secondary">
              Mise à jour le {new Date(task.updatedAt).toLocaleDateString('fr-FR')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          <RowActions
            task={task}
            onStatusChange={onStatusChange}
            onDelete={onDelete}
            onVerify={onVerify}
          />
        </Box>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}
